import { memo, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'

interface AnimatedCloudPoofProps {
  isActive?: boolean
  className?: string
}

export const AnimatedCloudPoof = memo<AnimatedCloudPoofProps>(({ isActive = true, className = '' }) => {
  const particleConfigs = useMemo(() => {
    const NUM_PARTICLES = 180
    return Array.from({ length: NUM_PARTICLES }, (_, i) => {
      const angle = (i / NUM_PARTICLES) * 2 * Math.PI
      const distance = 8 + Math.random() * 8
      const size = 2 + Math.random() * 2
      const delay = i * 0.01
      const opacity = 0.15 + Math.random() * 0.15
      const duration = 2 + Math.random() * 2
      return { angle, distance, size, delay, opacity, duration }
    })
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && !document.getElementById('nav-chalky-poof-style')) {
      const style = document.createElement('style')
      style.id = 'nav-chalky-poof-style'
      style.innerHTML = `
        @keyframes nav-chalky-poof {
          0% {
            opacity: 0;
            transform: translate(0, 0) scale(0.5);
          }
          15% {
            opacity: var(--particle-opacity);
            transform: translate(calc(var(--poof-tx) * 0.3), calc(var(--poof-ty) * 0.3)) scale(0.8);
          }
          50% {
            opacity: calc(var(--particle-opacity) * 0.8);
            transform: translate(calc(var(--poof-tx) * 0.7), calc(var(--poof-ty) * 0.7)) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(var(--poof-tx), var(--poof-ty)) scale(1.2);
          }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <motion.div
      className={`pointer-events-none absolute inset-0 -z-50 ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0 }}
      transition={{ duration: 1.2, ease: 'easeInOut' }}
    >
      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-1/2 top-1/2 -z-50 w-full rotate-[10deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}
      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-1/3 top-5 -z-50 w-full rotate-[75deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}
      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-1/4 top-1/2 -z-50 w-full rotate-[30deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-1/3 top-2 -z-50 w-full rotate-[250deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-2/3 top-5 -z-50 w-full rotate-[120deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-3/4 top-1/2 -z-50 w-full rotate-[320deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-1/2 top-2 -z-50 w-full rotate-[70deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute right-5 top-3 -z-50 w-full rotate-[30deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute right-2 top-1/2 -z-50 w-full rotate-[30deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}

      {isActive &&
        particleConfigs.map((particle, idx) => (
          <div
            key={idx}
            className="absolute left-3 top-1/2 -z-50 w-full rotate-[30deg] rounded-full bg-sketch-pink"
            style={
              {
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                '--particle-opacity': particle.opacity,
                '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                filter: 'blur(0.3px) contrast(1.1)',
              } as React.CSSProperties
            }
          />
        ))}
    </motion.div>
  )
})

AnimatedCloudPoof.displayName = 'AnimatedCloudPoof'
